import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
} from '@material-ui/core'
import { createStyles, makeStyles } from '@material-ui/core/styles'

import { TitleEdit } from '../components/core/TitleEdit'
import {
  setStoreCore,
  setStoreAvailableOnly,
  setgiftInShop,
  setStoreID,
  setEnableTextToSpeech,
} from '../features/store/storeSlice'

import { useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { getCoreList, getStoreNames } from '../features/store/storeListSlice'

const useStyles = makeStyles(() =>
  createStyles({
    storeFormItem: {
      display: 'flex',
      flexDirection: 'column',
      flexBasis: '45%',
      alignItems: 'center',
      '& .MuiFormControl-root': {
        width: '100%',
      },
    },
    menuItem: {
      fontSize: '1.35rem',
    },
    infoIconContainer: {
      display: 'flex',
      flexDirection: 'row',
    },
  })
)

interface Props {
  onValidation: (value: boolean) => void
}

export const DigitalWindowStore: React.FC<Props> = ({ onValidation }) => {
  const styles = useStyles()
  const dispatch = useDispatch()

  const {
    core,
    storeID,
    storeAvailableOnly,
    giftInShop,
    coreList,
    storeNames,
    storeNamesLoaded,
    enableTextToSpeech,
  } = useSelector(
    (state) => ({
      isEditExp: !!state.listing.edittableUuid || false,
      core: state.vmData.store.core || '',
      storeID: state.vmData.store.storeId || '',
      storeAvailableOnly: state.vmData.store.storeAvailableOnly || false,
      giftInShop: state.vmData.store.giftInShop || false,
      coreList: state.storeList.coreList,
      storeNames: state.storeList.storeNames,
      storeNamesLoaded: state.storeList.storeNamesLoaded,
      enableTextToSpeech: state.vmData.store.enableTextToSpeech || false,
    }),
    shallowEqual
  )

  const [selectedStore, setSelectedStore] = useState<string>(core || '')

  const handleStoreChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const core = event.target.value as string
    setSelectedStore(core)
    const selected = coreList?.find((s) => s.core === core)

    dispatch(setStoreCore(selected?.core || ''))
  }

  const handleStoreNameChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    const storeName = event.target.value as string
    console.log('Selected store name:', storeName)
    dispatch(setStoreID(storeName))
  }

  const handleStoreAvailableChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(setStoreAvailableOnly(event.target.checked))
  }

  const handleGiftShopChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setgiftInShop(event.target.checked))
  }

  const handleEnableTextToSpeech = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(setEnableTextToSpeech(event.target.checked))
  }

  useEffect(() => {
    if (coreList.length === 0) dispatch(getCoreList())
  }, [dispatch, coreList.length])

  useEffect(() => {
    if (!storeNamesLoaded && !!core) {
      dispatch(getStoreNames(core))
    }
  }, [core, dispatch, storeNamesLoaded])

  useEffect(() => {
    onValidation((!!core && !!storeID) || !core)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [core, storeID])

  return (
    <>
      {/* Store */}
      <Box className={styles.storeFormItem}>
        <Box className={styles.infoIconContainer}>
          <TitleEdit title="Store" />
        </Box>
        <FormControl variant="outlined">
          <InputLabel id="select-store">Select</InputLabel>
          <Select
            labelId="select-store"
            id="select-store"
            value={core}
            onChange={handleStoreChange}
            label="Select"
          >
            {[...coreList]
              .sort((a, b) => a.description.localeCompare(b.description))
              .map((store) => (
                <MenuItem
                  key={store.core}
                  value={store.core}
                  className={styles.menuItem}
                >
                  {store.description}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Box>
      {/* Banner Store*/}
      <Box className={styles.storeFormItem}>
        <Box className={styles.infoIconContainer}>
          <TitleEdit title="Store ID" />
        </Box>
        <FormControl
          variant="outlined"
          disabled={!selectedStore || !storeNamesLoaded}
        >
          <InputLabel id="select-store-name">Select Store</InputLabel>
          <Select
            labelId="select-store-name"
            id="select-store-name"
            value={storeID}
            onChange={handleStoreNameChange}
            label="Select Store ID"
          >
            {storeNames.map((store) => (
              <MenuItem key={store} value={store} className={styles.menuItem}>
                {store}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {/* Store Available */}
      <Box className={styles.storeFormItem}>
        <Box className={styles.infoIconContainer}>
          <TitleEdit title="Store Availability" />
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={storeAvailableOnly}
              onChange={handleStoreAvailableChange}
              name="storeAvailableOnly"
              color="primary"
              size="medium"
            />
          }
          label=""
          labelPlacement="top"
        />
      </Box>
      {/* Gift in Shop */}
      <Box className={styles.storeFormItem}>
        <Box className={styles.infoIconContainer}>
          <TitleEdit title="Gift shop" />
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={giftInShop}
              onChange={handleGiftShopChange}
              name="giftInShop"
              color="primary"
              size="medium"
            />
          }
          label=""
          labelPlacement="top"
        />
      </Box>
      {/* Enable Text-to-Speech */}
      <Box className={styles.storeFormItem}>
        <Box className={styles.infoIconContainer}>
          <TitleEdit title="Enable Text-to-Speech" />
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={enableTextToSpeech}
              onChange={handleEnableTextToSpeech}
              name="textToSpeech"
              color="primary"
              size="medium"
            />
          }
          label=""
          labelPlacement="top"
        />
      </Box>
    </>
  )
}
